import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useSearchParams } from "react-router-dom";
import { Badge, Button, Input } from "@vimos/ui";
import {
  DIRECTORY_EDITABLE_FEATURES,
  effectiveInvitationStatus,
  isInvitationUsable,
  type DirectoryInvitationStatus,
} from "@vimos/api-client";
import {
  createEditInvitation,
  createInvitation,
  fetchDirectoryPlans,
  fetchDirectoryStoreTarget,
  fetchInvitationsPage,
  revokeInvitation,
  searchDirectoryStoreTargets,
  type DirectoryStoreTarget,
  type CreatedInvitation,
  type DirectoryPlanOption,
} from "./directoryApi";
import { BillingFields, billingTermsForPlan, emptyBillingTerms } from "./BillingFields";

function parseFee(value: string, label: string): number {
  if (!value) return 0;
  const amount = Number(value);
  if (!Number.isFinite(amount) || amount <= 0) throw new Error(`${label} debe ser mayor que cero.`);
  return amount;
}

const expiryOptions = [
  { value: "24h", label: "24 horas" },
  { value: "7d", label: "7 dias" },
  { value: "custom", label: "Fecha personalizada" },
] as const;

const statusLabel: Record<DirectoryInvitationStatus, string> = {
  pending: "Pendiente",
  submitted: "Recibida",
  revoked: "Revocada",
  expired: "Caducada",
};

const editFeatureLabels: Record<(typeof DIRECTORY_EDITABLE_FEATURES)[number], string> = {
  "edit.name": "Nombre comercial",
  "edit.description": "Descripción",
  "edit.category": "Categoría",
  "edit.keywords": "Palabras clave",
  "edit.contact": "Teléfono, WhatsApp y página web",
  "edit.location": "Ubicación y dirección",
  "edit.images": "Logo, portada y galería",
  "edit.profile_content": "Horarios y productos o servicios",
};

const socialFeatureLabels = {
  "social.tiktok": "TikTok",
  "social.instagram": "Instagram",
  "social.facebook": "Facebook",
} as const;
const allSocialFeatures = Object.keys(socialFeatureLabels);

function isPremiumPlan(plan: DirectoryPlanOption): boolean {
  return plan.directory_priority > 0 || /premium|premiun/i.test(`${plan.name} ${plan.slug}`);
}

function templateFeatures(plan: DirectoryPlanOption): string[] {
  return [
    "profile.configured",
    ...(isPremiumPlan(plan) ? ["profile.hours", "profile.offerings", ...allSocialFeatures] : []),
  ];
}

export function InvitationsPanel() {
  const queryClient = useQueryClient();
  const [searchParams] = useSearchParams();
  const requestedEditStoreId =
    searchParams.get("mode") === "edit" ? (searchParams.get("store_id") ?? "") : "";
  const [page, setPage] = useState(1);
  const { data, isLoading, error } = useQuery({
    queryKey: ["directory_invitations", page],
    queryFn: () => fetchInvitationsPage(page),
  });
  const invitations = data?.items ?? [];
  const totalPages = Math.max(1, Math.ceil((data?.total ?? 0) / 20));
  const [label, setLabel] = useState("");
  const [singleUse, setSingleUse] = useState(true);
  const [expiry, setExpiry] = useState<(typeof expiryOptions)[number]["value"]>("7d");
  const [customExpiry, setCustomExpiry] = useState("");
  const [features, setFeatures] = useState<string[]>([]);
  const [created, setCreated] = useState<CreatedInvitation | null>(null);
  const [copied, setCopied] = useState(false);
  const [storeSearch, setStoreSearch] = useState("");
  const [targetStoreId, setTargetStoreId] = useState(requestedEditStoreId);
  const [editExpiry, setEditExpiry] = useState<(typeof expiryOptions)[number]["value"]>("7d");
  const [editCustomExpiry, setEditCustomExpiry] = useState("");
  const [editFeatures, setEditFeatures] = useState<string[]>([]);
  const [createdEdit, setCreatedEdit] = useState<CreatedInvitation | null>(null);
  const [editCopied, setEditCopied] = useState(false);
  const [billingTerms, setBillingTerms] = useState(emptyBillingTerms);
  const [targetStore, setTargetStore] = useState<DirectoryStoreTarget | null>(null);
  const plansQuery = useQuery({
    queryKey: ["directory_public_plans_for_contract"],
    queryFn: fetchDirectoryPlans,
    staleTime: 5 * 60 * 1000,
  });
  const basicPlan = plansQuery.data?.find((plan) => !isPremiumPlan(plan));
  const premiumPlan = plansQuery.data?.find(isPremiumPlan);
  const selectedTemplatePlan = plansQuery.data?.find((plan) => plan.id === billingTerms.planId) ?? null;

  function selectTemplate(plan: DirectoryPlanOption) {
    setFeatures(templateFeatures(plan));
    setBillingTerms((current) => billingTermsForPlan(current, plan));
  }

  function syncFeaturesToPlan(plan: DirectoryPlanOption | null) {
    if (plan) setFeatures(templateFeatures(plan));
  }

  const preselectedStore = useQuery({
    queryKey: ["directory_store_target", requestedEditStoreId],
    queryFn: () => fetchDirectoryStoreTarget(requestedEditStoreId),
    enabled: Boolean(requestedEditStoreId),
  });

  useEffect(() => {
    setTargetStoreId(requestedEditStoreId);
    setTargetStore(null);
    setStoreSearch("");
    setEditFeatures([]);
    setCreatedEdit(null);
  }, [requestedEditStoreId]);

  useEffect(() => {
    const store = preselectedStore.data;
    if (!store) return;
    setTargetStore(store);
    setTargetStoreId(store.id);
    setStoreSearch(store.name);
    setEditFeatures(
      store.origin === "saas"
        ? allSocialFeatures
        : [
            ...DIRECTORY_EDITABLE_FEATURES.filter((feature) => feature !== "edit.images"),
            ...allSocialFeatures,
          ],
    );
    if (requestedEditStoreId) {
      window.setTimeout(
        () =>
          document
            .getElementById("edit-link-generator")
            ?.scrollIntoView({ behavior: "smooth", block: "start" }),
        80,
      );
    }
  }, [preselectedStore.data, requestedEditStoreId]);

  const {
    data: storeMatches,
    isLoading: isLoadingStores,
    error: storesError,
  } = useQuery({
    queryKey: ["directory_store_targets", storeSearch.trim()],
    queryFn: () => searchDirectoryStoreTargets(storeSearch),
    enabled: storeSearch.trim().length >= 2,
  });

  const availableEditFeatures =
    targetStore?.origin === "saas"
      ? Object.entries(socialFeatureLabels)
      : [
          ...DIRECTORY_EDITABLE_FEATURES.map((feature) => [feature, editFeatureLabels[feature]] as const),
          ...Object.entries(socialFeatureLabels),
        ];
  const availableEditFeatureKeys = availableEditFeatures.map(([feature]) => feature);

  function setEditProfile(profile: "complete" | "main" | "social" | "none") {
    if (profile === "complete") {
      setEditFeatures(availableEditFeatureKeys);
    } else if (profile === "main") {
      setEditFeatures(availableEditFeatureKeys.filter((feature) => feature !== "edit.images"));
    } else if (profile === "social") {
      setEditFeatures(availableEditFeatureKeys.filter((feature) => feature.startsWith("social.")));
    } else {
      setEditFeatures([]);
    }
  }

  const create = useMutation({
    mutationFn: () => {
      const setupAmount = parseFee(billingTerms.setupAmount, "El cobro de alta");
      const monthlyAmount = parseFee(billingTerms.monthlyAmount, "La mensualidad");
      const yearlyAmount = parseFee(billingTerms.yearlyAmount, "La anualidad");
      if (billingTerms.isComped && !billingTerms.priceNote.trim()) {
        throw new Error("Indica el motivo de la cortesía para dejarla registrada.");
      }
      const agreedPlanAmount =
        billingTerms.planPeriod === "one_time"
          ? setupAmount
          : billingTerms.planPeriod === "yearly"
            ? yearlyAmount
            : monthlyAmount;
      if (!billingTerms.isComped && billingTerms.planId && agreedPlanAmount <= 0) {
        throw new Error("El plan seleccionado requiere un monto acordado mayor que cero.");
      }
      if (
        billingTerms.planId &&
        !billingTerms.isComped &&
        agreedPlanAmount !== Number(billingTerms.planPrice) &&
        !billingTerms.priceNote.trim()
      ) {
        throw new Error("Registra el motivo del precio especial para dejar trazabilidad.");
      }
      return createInvitation({
        label,
        singleUse,
        expiry,
        customExpiry: expiry === "custom" ? customExpiry : undefined,
        features,
        billingPlanId: billingTerms.planId || null,
        billingPlanName: billingTerms.planName || null,
        billingPlanPrice: billingTerms.planPrice ? Number(billingTerms.planPrice) : null,
        billingPlanPeriod: billingTerms.planPeriod || null,
        billingPlanSnapshot: billingTerms.planSnapshot,
        billingIsComped: billingTerms.isComped,
        billingPriceNote: billingTerms.priceNote.trim() || null,
        billingSetupAmount: setupAmount,
        billingSetupDueDate: setupAmount ? billingTerms.setupDueDate : undefined,
        billingMonthlyAmount: monthlyAmount,
        billingYearlyAmount: yearlyAmount,
        billingStartDate:
          monthlyAmount ||
          yearlyAmount ||
          (billingTerms.isComped &&
            (billingTerms.planPeriod === "monthly" || billingTerms.planPeriod === "yearly"))
            ? billingTerms.monthlyStartDate
            : undefined,
      });
    },
    onSuccess: (result) => {
      setCreated(result);
      setPage(1);
      setLabel("");
      setFeatures([]);
      setBillingTerms(emptyBillingTerms());
      setCopied(false);
      queryClient.invalidateQueries({ queryKey: ["directory_invitations"] });
    },
  });

  const revoke = useMutation({
    mutationFn: (id: string) => revokeInvitation(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["directory_invitations"] });
      queryClient.invalidateQueries({ queryKey: ["directory_billing_charges"] });
    },
  });

  const createEdit = useMutation({
    mutationFn: () => {
      if (!targetStore) throw new Error("Selecciona una ficha existente.");
      const monthlyAmount = parseFee(billingTerms.monthlyAmount, "La mensualidad");
      return createEditInvitation({
        storeId: targetStore.id,
        label: targetStore.name,
        expiry: editExpiry,
        customExpiry: editExpiry === "custom" ? editCustomExpiry : undefined,
        features: editFeatures,
        billingServiceAmount: parseFee(billingTerms.serviceAmount, "El cobro por edición"),
        billingMonthlyAmount: monthlyAmount,
        billingStartDate: monthlyAmount ? billingTerms.monthlyStartDate : undefined,
      });
    },
    onSuccess: (result) => {
      setCreatedEdit(result);
      setPage(1);
      setEditCopied(false);
      queryClient.invalidateQueries({ queryKey: ["directory_invitations"] });
      queryClient.invalidateQueries({ queryKey: ["directory_billing_charges"] });
    },
  });

  async function copyLink(url: string) {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
    } catch {
      setCopied(false);
    }
  }

  async function copyEditLink(url: string) {
    try {
      await navigator.clipboard.writeText(url);
      setEditCopied(true);
    } catch {
      setEditCopied(false);
    }
  }

  return (
    <div className="mt-4">
      <div className="border-sand/50 bg-paper rounded-lg border p-4 sm:p-6">
        <h2 className="font-display text-ink text-lg font-bold">Generar invitacion</h2>
        <p className="text-mist mt-1 text-sm">
          Comparte el enlace con el dueno del negocio para que complete su ficha. El enlace solo se muestra
          una vez.
        </p>

        <div className="mt-4 grid gap-4 sm:grid-cols-3">
          <Input
            label="Referencia (opcional)"
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            placeholder="Ej. Panaderia Lopez"
          />
          <div className="flex flex-col gap-1.5">
            <label className="font-body text-ink text-sm font-medium">Vigencia</label>
            <select
              className="border-sand/60 bg-paper text-ink h-11 rounded-md border px-2 text-sm"
              value={expiry}
              onChange={(e) => setExpiry(e.target.value as typeof expiry)}
            >
              {expiryOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
          {expiry === "custom" ? (
            <Input
              label="Vence el"
              type="datetime-local"
              value={customExpiry}
              onChange={(e) => setCustomExpiry(e.target.value)}
            />
          ) : null}
        </div>

        <label className="text-ink mt-4 flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            className="h-4 w-4"
            checked={singleUse}
            onChange={(e) => setSingleUse(e.target.checked)}
          />
          Un solo uso (solo puede enviarse una vez)
        </label>

        <fieldset className="border-sand/40 mt-4 rounded-md border p-3 sm:p-4">
          <legend className="font-body text-ink px-1 text-sm font-medium">Plantilla de ficha</legend>
          <p className="text-mist mb-3 text-xs">
            Elige el plan y el formulario quedará preparado. Puedes ajustar qué módulos tendrá esta ficha.
          </p>
          <div className="grid gap-3 sm:grid-cols-2">
            {[
              {
                plan: basicPlan,
                title: "Ficha Comercial Básica",
                detail: "Datos esenciales, contactos y fotos.",
              },
              {
                plan: premiumPlan,
                title: "Ficha Comercial Premium",
                detail: "Incluye horario, productos o servicios y redes.",
              },
            ].map(({ plan, title, detail }) => (
              <button
                key={title}
                type="button"
                disabled={!plan}
                aria-pressed={Boolean(plan && selectedTemplatePlan?.id === plan.id)}
                onClick={() => plan && selectTemplate(plan)}
                className={`rounded-lg border p-3 text-left transition ${
                  plan && selectedTemplatePlan?.id === plan.id
                    ? "border-gold bg-gold/10 ring-gold ring-1"
                    : "border-sand/50 hover:border-gold/70"
                } disabled:cursor-not-allowed disabled:opacity-50`}
              >
                <span className="text-ink block text-sm font-semibold">{title}</span>
                <span className="text-mist mt-1 block text-xs">
                  {plan ? detail : "No se encontró este plan publicado."}
                </span>
                {plan ? (
                  <span className="text-ink mt-2 block text-sm font-semibold">
                    S/ {plan.price_amount.toFixed(2)}{" "}
                    <span className="text-mist text-xs font-normal">
                      por{" "}
                      {plan.billing_period === "monthly"
                        ? "30 días"
                        : plan.billing_period === "yearly"
                          ? "año"
                          : "única vez"}
                    </span>
                  </span>
                ) : null}
              </button>
            ))}
          </div>
          {selectedTemplatePlan ? (
            <div className="border-sand/40 mt-4 rounded-md border p-3">
              <p className="text-ink text-sm font-medium">Personaliza esta ficha</p>
              <p className="text-mist mb-3 text-xs">
                El plan sigue registrado aunque ajustes estos módulos para este negocio.
              </p>
              <div className="grid gap-x-5 gap-y-2 sm:grid-cols-2">
                {(
                  [
                    ["profile.hours", "Horario de atención"],
                    ["profile.offerings", "Productos o servicios"],
                  ] as const
                ).map(([feature, label]) => (
                  <label key={feature} className="text-ink flex items-center gap-2 text-sm">
                    <input
                      type="checkbox"
                      className="h-4 w-4 accent-[#C9A24D]"
                      checked={features.includes(feature)}
                      onChange={(event) =>
                        setFeatures((current) =>
                          event.target.checked
                            ? [...current, feature]
                            : current.filter((item) => item !== feature),
                        )
                      }
                    />
                    {label}
                  </label>
                ))}
                <div className="sm:col-span-2">
                  <p className="text-ink mb-2 text-sm">Redes sociales</p>
                  <div className="flex flex-wrap gap-x-5 gap-y-2">
                    {Object.entries(socialFeatureLabels).map(([feature, label]) => (
                      <label key={feature} className="text-ink flex items-center gap-2 text-sm">
                        <input
                          type="checkbox"
                          className="h-4 w-4 accent-[#C9A24D]"
                          checked={features.includes(feature)}
                          onChange={(event) =>
                            setFeatures((current) =>
                              event.target.checked
                                ? [...current, feature]
                                : current.filter((item) => item !== feature),
                            )
                          }
                        />
                        {label}
                      </label>
                    ))}
                  </div>
                </div>
              </div>
              {isPremiumPlan(selectedTemplatePlan) ? (
                <p className="text-mist mt-3 text-xs">
                  Premium: quedará con prioridad de búsqueda al aprobarse. Puedes administrarla después desde
                  la tabla general.
                </p>
              ) : null}
            </div>
          ) : (
            <p className="mt-3 text-xs text-amber-700">Selecciona Básica o Premium para generar el enlace.</p>
          )}
        </fieldset>

        <BillingFields
          mode="new-listing"
          value={billingTerms}
          onChange={setBillingTerms}
          onPlanChange={syncFeaturesToPlan}
        />

        {create.error ? (
          <p className="mt-3 text-sm text-red-600">
            {create.error instanceof Error ? create.error.message : "No se pudo generar la invitacion."}
          </p>
        ) : null}

        <div className="mt-4">
          <Button
            onClick={() => create.mutate()}
            isLoading={create.isPending}
            disabled={!selectedTemplatePlan || create.isPending}
          >
            Generar enlace
          </Button>
        </div>

        {created ? (
          <div className="border-gold/40 bg-signal/10 mt-4 rounded-md border p-3">
            <p className="text-ink text-sm font-medium">Enlace de invitacion (guardalo ahora)</p>
            <div className="mt-2 flex flex-col gap-2 sm:flex-row sm:items-center">
              <code className="bg-paper border-sand/60 text-ink flex-1 overflow-x-auto rounded border px-2 py-1 text-xs">
                {created.url}
              </code>
              <Button size="sm" variant="outline" onClick={() => copyLink(created.url)}>
                {copied ? "Copiado" : "Copiar"}
              </Button>
            </div>
          </div>
        ) : null}
      </div>

      <div
        id="edit-link-generator"
        className="border-sand/50 bg-paper mt-5 scroll-mt-6 rounded-lg border p-4 sm:p-6"
      >
        <h2 className="font-display text-ink text-lg font-bold">Generar enlace de edición</h2>
        <p className="text-mist mt-1 text-sm">
          El cliente actualizará únicamente los campos autorizados. Sus cambios quedarán pendientes de
          revisión antes de publicarse.
        </p>

        <div className="mt-4 grid gap-4 sm:grid-cols-2">
          <Input
            label="Buscar ficha existente"
            value={storeSearch}
            onChange={(event) => {
              setStoreSearch(event.target.value);
              setTargetStore(null);
              setTargetStoreId("");
            }}
            placeholder="Nombre, ciudad o slug"
          />
          <div className="flex flex-col gap-1.5">
            <label htmlFor="edit-invitation-store" className="font-body text-ink text-sm font-medium">
              Negocio que se editará
            </label>
            <select
              id="edit-invitation-store"
              className="border-sand/60 bg-paper text-ink h-11 rounded-md border px-2 text-sm"
              value={targetStoreId}
              disabled={isLoadingStores || Boolean(storesError) || storeSearch.trim().length < 2}
              onChange={(event) => {
                const nextStoreId = event.target.value;
                const nextStore = storeMatches?.find((store) => store.id === nextStoreId) ?? null;
                setTargetStoreId(nextStoreId);
                setTargetStore(nextStore);
                const nextFeatures =
                  nextStore?.origin === "saas"
                    ? allSocialFeatures
                    : [...DIRECTORY_EDITABLE_FEATURES, ...allSocialFeatures];
                setEditFeatures(nextFeatures);
                setBillingTerms(emptyBillingTerms());
                setCreatedEdit(null);
              }}
            >
              <option value="">
                {isLoadingStores
                  ? "Buscando fichas…"
                  : storeSearch.trim().length < 2
                    ? "Escribe al menos 2 caracteres"
                    : "Selecciona una ficha"}
              </option>
              {storeMatches?.map((store) => (
                <option key={store.id} value={store.id}>
                  {store.name} · {store.city || store.slug}
                </option>
              ))}
              {targetStore && !storeMatches?.some((store) => store.id === targetStore.id) ? (
                <option value={targetStore.id}>
                  {targetStore.name} · {targetStore.city || targetStore.slug}
                </option>
              ) : null}
            </select>
          </div>
        </div>
        {storesError ? <p className="mt-2 text-sm text-red-600">No se pudieron buscar las fichas.</p> : null}
        {preselectedStore.error ? (
          <p className="mt-2 text-sm text-red-600">
            No se pudo cargar el negocio indicado desde el directorio. Puedes buscarlo y seleccionarlo
            manualmente.
          </p>
        ) : null}
        {targetStore ? (
          <p className="text-mist mt-2 text-xs">
            Enlace asociado a <span className="text-ink font-medium">{targetStore.name}</span> ·{" "}
            {targetStore.origin === "manual" ? "Ficha manual" : "Ficha sincronizada con el SaaS"}
          </p>
        ) : null}

        <div className="mt-4 grid gap-4 sm:grid-cols-2">
          <div className="flex flex-col gap-1.5">
            <label htmlFor="edit-invitation-expiry" className="font-body text-ink text-sm font-medium">
              Vigencia del enlace
            </label>
            <select
              id="edit-invitation-expiry"
              className="border-sand/60 bg-paper text-ink h-11 rounded-md border px-2 text-sm"
              value={editExpiry}
              onChange={(event) => setEditExpiry(event.target.value as typeof editExpiry)}
            >
              {expiryOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
          {editExpiry === "custom" ? (
            <Input
              label="Vence el"
              type="datetime-local"
              value={editCustomExpiry}
              onChange={(event) => setEditCustomExpiry(event.target.value)}
            />
          ) : null}
        </div>

        {targetStore ? (
          <fieldset className="border-sand/40 mt-4 rounded-md border p-3">
            <legend className="font-body text-ink px-1 text-sm font-medium">
              Campos que puede actualizar el cliente
            </legend>
            <p className="text-mist mb-3 text-xs">
              Se activa el perfil completo permitido para esta ficha. Elige un perfil rápido o ajusta los
              campos individualmente.
            </p>
            <div className="mb-3 flex flex-wrap gap-2">
              <Button size="sm" variant="outline" onClick={() => setEditProfile("complete")}>
                Ficha completa ({availableEditFeatureKeys.length})
              </Button>
              <Button size="sm" variant="outline" onClick={() => setEditProfile("main")}>
                Datos principales
              </Button>
              <Button size="sm" variant="outline" onClick={() => setEditProfile("social")}>
                Solo redes
              </Button>
              <Button size="sm" variant="outline" onClick={() => setEditProfile("none")}>
                Ninguno
              </Button>
            </div>
            <div className="grid gap-x-5 gap-y-2 sm:grid-cols-2">
              {availableEditFeatures.map(([feature, featureLabel]) => (
                <label key={feature} className="text-ink flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    className="h-4 w-4 accent-[#C9A24D]"
                    checked={editFeatures.includes(feature)}
                    onChange={(event) =>
                      setEditFeatures((current) =>
                        event.target.checked
                          ? [...current, feature]
                          : current.filter((item) => item !== feature),
                      )
                    }
                  />
                  {featureLabel}
                </label>
              ))}
            </div>
          </fieldset>
        ) : null}

        {targetStore ? (
          <BillingFields mode="edit-service" value={billingTerms} onChange={setBillingTerms} />
        ) : null}

        {createEdit.error ? (
          <p className="mt-3 text-sm text-red-600">
            {createEdit.error instanceof Error
              ? createEdit.error.message
              : "No se pudo generar el enlace de edición."}
          </p>
        ) : null}
        <div className="mt-4">
          <Button
            onClick={() => createEdit.mutate()}
            isLoading={createEdit.isPending}
            disabled={!targetStore || editFeatures.length === 0 || createEdit.isPending}
          >
            Generar enlace de edición
          </Button>
        </div>
        {createdEdit ? (
          <div className="border-gold/40 bg-signal/10 mt-4 rounded-md border p-3">
            <p className="text-ink text-sm font-medium">Enlace de edición (cópialo ahora)</p>
            <div className="mt-2 flex flex-col gap-2 sm:flex-row sm:items-center">
              <code className="bg-paper border-sand/60 text-ink flex-1 overflow-x-auto rounded border px-2 py-1 text-xs">
                {createdEdit.url}
              </code>
              <Button size="sm" variant="outline" onClick={() => copyEditLink(createdEdit.url)}>
                {editCopied ? "Copiado" : "Copiar"}
              </Button>
            </div>
          </div>
        ) : null}
      </div>

      {isLoading ? <p className="text-mist mt-6">Cargando invitaciones...</p> : null}
      {error ? <p className="mt-6 text-red-600">No pudimos cargar las invitaciones.</p> : null}

      <ul className="divide-sand/40 border-sand/40 bg-paper mt-6 divide-y rounded-lg border">
        {invitations.length === 0 ? (
          <li className="text-mist px-4 py-6 text-sm">Todavia no hay invitaciones.</li>
        ) : null}
        {invitations.map((invitation) => {
          const status = effectiveInvitationStatus(invitation);
          const completedEdit =
            invitation.mode === "edit" &&
            ["approved", "rejected"].includes(invitation.submission_status ?? "");
          const usable = isInvitationUsable(invitation) && !completedEdit;
          const invitationStatusLabel =
            invitation.mode === "edit"
              ? status === "pending"
                ? "Vigente"
                : status === "submitted"
                  ? completedEdit
                    ? "Completado"
                    : "En revisión"
                  : statusLabel[status]
              : statusLabel[status];
          const socialFeatures = (invitation.features ?? [])
            .filter((feature) => feature.startsWith("social."))
            .map((feature) => feature.slice("social.".length))
            .map((network) =>
              network === "tiktok"
                ? "TikTok"
                : network === "instagram"
                  ? "Instagram"
                  : network === "facebook"
                    ? "Facebook"
                    : null,
            )
            .filter((network): network is "TikTok" | "Instagram" | "Facebook" => network !== null);
          const editFeatures = (invitation.features ?? [])
            .filter((feature): feature is (typeof DIRECTORY_EDITABLE_FEATURES)[number] =>
              DIRECTORY_EDITABLE_FEATURES.includes(feature as (typeof DIRECTORY_EDITABLE_FEATURES)[number]),
            )
            .map((feature) => editFeatureLabels[feature]);
          const billingTerms = [
            invitation.billing_plan_name ? `Plan ${invitation.billing_plan_name}` : null,
            invitation.billing_is_comped ? "Cortesía gratuita" : null,
            Number(invitation.billing_setup_amount) > 0
              ? `Alta ${new Intl.NumberFormat("es-PE", { style: "currency", currency: "PEN" }).format(Number(invitation.billing_setup_amount))}`
              : null,
            Number(invitation.billing_monthly_amount) > 0
              ? `Mensual ${new Intl.NumberFormat("es-PE", { style: "currency", currency: "PEN" }).format(Number(invitation.billing_monthly_amount))}`
              : null,
            Number(invitation.billing_yearly_amount) > 0
              ? `Anual ${new Intl.NumberFormat("es-PE", { style: "currency", currency: "PEN" }).format(Number(invitation.billing_yearly_amount))}`
              : null,
            Number(invitation.billing_service_amount) > 0
              ? `Edición ${new Intl.NumberFormat("es-PE", { style: "currency", currency: "PEN" }).format(Number(invitation.billing_service_amount))}`
              : null,
            invitation.billing_price_note ? `Precio acordado: ${invitation.billing_price_note}` : null,
          ].filter((term): term is string => term !== null);
          return (
            <li
              key={invitation.id}
              className="flex flex-col gap-3 px-4 py-3 lg:flex-row lg:items-center lg:justify-between"
            >
              <div>
                <p className="text-ink font-medium">
                  {invitation.target_store_id ? (
                    <Link
                      to={`/directorio/${invitation.target_store_id}`}
                      className="hover:text-blue-700 hover:underline"
                    >
                      {invitation.mode === "edit"
                        ? (invitation.label ?? "Edición de ficha")
                        : (invitation.label ?? `Invitacion ${invitation.token_prefix}...`)}
                    </Link>
                  ) : invitation.mode === "edit" ? (
                    (invitation.label ?? "Edición de ficha")
                  ) : (
                    (invitation.label ?? `Invitacion ${invitation.token_prefix}...`)
                  )}
                </p>
                <p className="text-mist text-sm">
                  {invitation.mode === "edit"
                    ? "Enlace de edición"
                    : invitation.single_use
                      ? "Un solo uso"
                      : "Reutilizable"}{" "}
                  - vence {new Date(invitation.expires_at).toLocaleDateString("es-PE")}
                  {invitation.used_count > 0 ? ` - usada ${invitation.used_count} vez/veces` : ""}
                </p>
                <p className="text-mist mt-1 text-xs">
                  {invitation.mode === "edit"
                    ? `Campos habilitados: ${[...editFeatures, ...socialFeatures].join(", ") || "ninguno"}`
                    : `Redes habilitadas: ${socialFeatures.length ? socialFeatures.join(", ") : "ninguna"}`}
                </p>
                {billingTerms.length > 0 ? (
                  <p className="text-mist mt-1 text-xs">Cobro registrado: {billingTerms.join(" · ")}</p>
                ) : null}
              </div>
              <div className="flex flex-wrap items-center gap-3">
                <Badge tone={status === "pending" ? "market" : status === "submitted" ? "signal" : "neutral"}>
                  {invitationStatusLabel}
                </Badge>
                {usable ? (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => revoke.mutate(invitation.id)}
                    disabled={revoke.isPending}
                  >
                    Revocar
                  </Button>
                ) : null}
              </div>
            </li>
          );
        })}
      </ul>
      {totalPages > 1 ? (
        <nav className="mt-4 flex items-center justify-between gap-3" aria-label="Paginación de invitaciones">
          <Button
            size="sm"
            variant="outline"
            disabled={page <= 1}
            onClick={() => setPage((current) => current - 1)}
          >
            Anterior
          </Button>
          <p className="text-mist text-sm">
            {data?.total ?? 0} enlaces · página {page} de {totalPages}
          </p>
          <Button
            size="sm"
            variant="outline"
            disabled={page >= totalPages}
            onClick={() => setPage((current) => current + 1)}
          >
            Siguiente
          </Button>
        </nav>
      ) : null}
    </div>
  );
}
